import { useEffect, useMemo } from "react";

import { isLoggedIn } from "@shapr3d/frontend-lib";
import queryString from "query-string";
import { useLocation, useSearchParams } from "react-router-dom";

import { ProjectViewLinkInvitationSignIn } from "../components/ProjectViewLinkInvitationSignIn/ProjectViewLinkInvitationSignIn";
import SignIn from "../components/SignIn/SignIn";
import { zendeskUrl } from "../config";
import { useLoginCredentials } from "../contexts/LoginCredentialsContext";
import useNavigation from "../hooks/navigation";
import { useLogin, useLogout } from "../hooks/useAuth";
import { useLoginRedirect } from "../hooks/useLoginRedirect";
import { AuthError, SearchParam } from "../types";
import { searchParamAsOptionalString } from "../urlUtils";
import { isEmailInvalid } from "../utils/validationUtils";

export interface SignInContainerProps {
  isAuthHandoffFailed?: boolean;
}

const SignInContainer: React.FC<SignInContainerProps> = ({ isAuthHandoffFailed }) => {
  const navigation = useNavigation();
  const location = useLocation();
  const { setCredentials } = useLoginCredentials();
  const [search] = useSearchParams();
  const isZendeskLogin = search.get(SearchParam.ReturnTo)?.startsWith(zendeskUrl);

  const appTrackingID = useMemo(() => {
    const queryParams = queryString.parse(location.search);
    return searchParamAsOptionalString(queryParams[SearchParam.TrackingID]);
  }, [location.search]);

  const {
    mutateAsync: mutateLogin,
    isPending: isLoginPending,
    error: loginError,
  } = useLogin({ appTrackingID });

  const redirectCallback = useLoginRedirect();

  useEffect(() => {
    if (loginError === AuthError.TOTPRequired) {
      navigation.goToTwoFactorVerify({ replace: true });
    }
  }, [loginError, navigation]);

  const signIn = async (email: string, password: string, captcha?: string) => {
    try {
      await mutateLogin({ email, password, captcha }, { onSuccess: redirectCallback });
    } catch (error) {
      if (error === AuthError.TOTPRequired) {
        setCredentials({ email, password });
      } else {
        setCredentials(null);
      }
    }
  };

  const { mutateAsync: mutateLogout } = useLogout({
    redirect: false,
    withRequest: false,
  });
  useEffect(() => {
    if (isLoggedIn()) {
      if (isAuthHandoffFailed) {
        mutateLogout();
      } else {
        redirectCallback();
      }
    }
  }, [redirectCallback, isAuthHandoffFailed, mutateLogout]);

  const emailHint = useMemo(() => {
    const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
    const emailHint = hashParams.get(SearchParam.EmailHint);
    if (!emailHint || isEmailInvalid(emailHint)) {
      return null;
    }

    return emailHint;
  }, [location]);

  if (emailHint) {
    return (
      <ProjectViewLinkInvitationSignIn
        submit={signIn}
        inProgress={isLoginPending}
        loginError={loginError}
        emailHint={emailHint}
      />
    );
  }

  return (
    <SignIn
      submit={signIn}
      inProgress={isLoginPending}
      loginError={loginError}
      hideRegistration={isZendeskLogin}
      isAuthHandoffFailed={isAuthHandoffFailed}
    />
  );
};

export default SignInContainer;
